import { useState, useEffect, useMemo } from 'react';

import { useApp } from '../../app/AppContext.jsx';
import { useCatalog } from '../../domains/catalog/context/CatalogContext.jsx';

import { useI18n } from '../i18n/useI18n.js';
import { useProductStrings } from '../i18n/useProductStrings.js';

import { CATALOG_DISPLAY_REGISTRY }     from '../../domains/catalog/registries/catalogDisplayRegistry.js';

import { formatCompartmentInsolation }  from '../utils/productFormatters.js';
import { getInsolationBucket }          from '../utils/productInsolation.js';
import {
	PREVIEW_UI_MAP,
	DEFAULT_PREVIEW_ITEMS, }            from '../registries/productPreviewDisplayRegistry.jsx';
import { COLOR_MODE }                   from '../constants/colorMode.js';
import { PRODUCT_ATTRIBUTE_IDS }        from '../constants/productAttributes.js';
import { AVAILABILITY_STATUS } from '../constants/availabilityStatus.js';

// ── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Maps a product.rooms count to the CSS var index: rooms-N → index N (1:1 for 1–10),
 * rooms-11+ wraps back to 1–10. Returns null for null/0 (no swatch).
*/
function roomsVarIndex(rooms) {
    if (rooms == null || rooms <= 0) return null
    if (rooms <= 10) return rooms
    return ((rooms - 1) % 10) + 1
}


// ── Hook ────────────────────────────────────────────────────────────────────
/**
 * @param {object}   props
 * @param {object}   props.product          - Product data object
 * @param {number}   [props.deferPlanMs=0]  - Delay thumb mount to reduce hover-preview churn
 * 
 * @returns {{
 * getProductsCategoryString,
 * gridItems:                   object[],
 * isOnOffer:                   boolean,
 * isPlanMounted:               boolean,
 * logoSrc:                     string,
 * planVisible:                 boolean,
 * projectDataUrl:              string,
 * showPlanThumb:               boolean,
 * }}
 */
export function useProductPreviewData({
	product,
	deferPlanMs = 0,
}) {
	const { t } = useI18n();
	const getProductsCategoryString = useProductStrings();

	const { appCapabilities, windowData, logoSrc } = useApp();
	const { catalogCapabilities, colorMode, colorModeDeciles, tiers } =
		useCatalog();

	// ── Derived state ───────────────────────────────────────────────────────────
	const { showPlanThumb, soldUnitsPlanVisible, reservedUnitsPlanVisible } = appCapabilities;

	const {
		hasMultipleBuildings,
		pricesReservedVisible,
		pricesSoldVisible,
		pricesVisible,
	} = catalogCapabilities;

	const pricesVisibility = {
		pricesReservedVisible,
		pricesSoldVisible,
		pricesVisible,
	};
	const projectDataUrl = windowData?.projectDataUrl ?? null;

    const planVisible = 
        product?.status === AVAILABILITY_STATUS.AVAILABLE ||
        (product?.status === AVAILABILITY_STATUS.RESERVED && reservedUnitsPlanVisible ) ||
        (product?.status === AVAILABILITY_STATUS.SOLD && soldUnitsPlanVisible )

	// 1. Lazy mount plan thumbnail
	const [isPlanMounted, setIsPlanMounted] = useState(deferPlanMs <= 0);

	useEffect(() => {
		if (!showPlanThumb || !projectDataUrl || !product?.uuid) {
			setIsPlanMounted(false);
			return;
		}
		if (deferPlanMs <= 0) {
			setIsPlanMounted(true);
			return;
		}

		setIsPlanMounted(false);
		const timeoutId = setTimeout(() => setIsPlanMounted(true), deferPlanMs);
		return () => clearTimeout(timeoutId);
	}, [showPlanThumb, projectDataUrl, deferPlanMs, product?.uuid]);

	// 2. Build grid items (Domain + CMS Overlay + Preview Extensions)
	const gridItems = useMemo(() => {
		if (!product) return [];

		// Pobieramy konfigurację z capabilities (np. ustawioną w CMS) lub używamy domyślnych id
		const configuredIds =
			catalogCapabilities?.previewItems ?? DEFAULT_PREVIEW_ITEMS;
		const categoryVarKey = product.product_category?.replace(/_/g, '-') ?? null;

		const context = {
			t,
			colorMode,
			colorModeDeciles,
			tiers,
			hasMultipleBuildings,
			pricesVisibility,
			getInsolationBucket,
			formatCompartmentInsolation,
			roomsVarIndex,
			categoryVarKey,
		};

		// A) Budujemy pola standardowe wyciągnięte z rejestru domenowego
		const items = configuredIds
			.map((id) => {
				const domainItem = CATALOG_DISPLAY_REGISTRY.find(
					(entry) => entry.id === id,
				);
				if (!domainItem) return null;

				// Pomijamy budynek, jeśli to inwestycja jednobudynkowa i nie jest wymuszona
				if (id === PRODUCT_ATTRIBUTE_IDS.BUILDING && !hasMultipleBuildings)
					return null;

				const uiDef = PREVIEW_UI_MAP[id];
				const value =
					typeof uiDef?.render === 'function'
						? uiDef.render(product, context)
						: domainItem.getValue(product, context);
				const span = uiDef?.span ?? 1;

				if (value == null) return null;

				return {
					id,
					label: t(domainItem.labelKey),
					value,
					span,
				};
			})
			.filter(Boolean);

		// B) Wstrzykujemy dynamiczne pola legendy (np. Usłonecznienie lub Porównanie)
		if (colorMode === COLOR_MODE.INSOLATION) {
			items.push({
				id: PRODUCT_ATTRIBUTE_IDS.INSOLATION_HOURS,
				label: t(
					PREVIEW_UI_MAP[PRODUCT_ATTRIBUTE_IDS.INSOLATION_HOURS].labelKey,
				),
				span: PREVIEW_UI_MAP[PRODUCT_ATTRIBUTE_IDS.INSOLATION_HOURS].span,
				value: PREVIEW_UI_MAP[PRODUCT_ATTRIBUTE_IDS.INSOLATION_HOURS].render(
					product,
					context,
				),
			});
		} else if (
			colorMode === COLOR_MODE.COMPARE &&
			colorModeDeciles?.has(product.uuid)
		) {
			items.push({
				id: PRODUCT_ATTRIBUTE_IDS.COMPARE_AREA,
				label: t(PREVIEW_UI_MAP[PRODUCT_ATTRIBUTE_IDS.COMPARE_AREA].labelKey),
				span: PREVIEW_UI_MAP[PRODUCT_ATTRIBUTE_IDS.COMPARE_AREA].span,
				value: PREVIEW_UI_MAP[PRODUCT_ATTRIBUTE_IDS.COMPARE_AREA].render(
					product,
					context,
				),
			});
		} else if (
			colorMode === COLOR_MODE.CATEGORY &&
			!!product.product_category
		) {
			items.push({
				id: PRODUCT_ATTRIBUTE_IDS.CATEGORY,
				label: t(PREVIEW_UI_MAP[PRODUCT_ATTRIBUTE_IDS.CATEGORY].labelKey),
				span: PREVIEW_UI_MAP[PRODUCT_ATTRIBUTE_IDS.CATEGORY].span,
				value: PREVIEW_UI_MAP[PRODUCT_ATTRIBUTE_IDS.CATEGORY].render(
					product,
					context,
				),
			});
		}

		return items;
	}, [
		product,
		catalogCapabilities,
		colorMode,
		colorModeDeciles,
		tiers,
		hasMultipleBuildings,
		t,
	]);

	return {
		getProductsCategoryString,
		gridItems,
		isOnOffer: Boolean(product?.price_offer?.on_offer),
		isPlanMounted,
		logoSrc,
        planVisible,
		projectDataUrl,
		showPlanThumb,
	};
}
